//----------------CORE-----------------//
// Remembers where the user was headed when the app ejected them to /auth/login,
// so that sign-in (and the 2FA hop) can land them there instead of the
// /solutions default. Written for alarm-email deep links, which arrive at a
// device URL on a browser that is usually logged out.
//
// sessionStorage, not the alternatives: Redux dies on the full-page navigation
// every eject site performs (there is no redux-persist), router location.state
// is annihilated by that same navigation, and a ?next= query param would put an
// attacker-controllable destination on a public page. sessionStorage survives
// the navigation and a hard reload, and dies with the tab, so a stale
// destination cannot haunt a later session in the same browser.

const KEY = "xlr_postLoginRedirect";

// Must comfortably exceed the 2FA code budget (twoFACodeExpiryTime defaults to
// 15 minutes in licensingMS), or a user who takes their time over the emailed
// code would silently lose the destination they clicked.
const TTL_MS = 30 * 60 * 1000;

// Characters legal in an in-app path or query string. An allowlist rather than a
// control-character blocklist: whitespace, CR/LF, quotes, angle brackets and
// backslashes are all excluded by construction rather than by enumeration.
const SAFE_PATH = /^[A-Za-z0-9_\-.~/%?&=+:@,;'!$()*]+$/;

// Same-origin, in-app paths only.
//   "//evil.com" and "/\evil.com" are protocol-relative and resolve off-origin.
//   /auth/* is where we eject TO — stashing it would overwrite the real
//   destination with the login page itself.
export function isSafeRedirect(path) {
  if (typeof path !== "string" || path.length === 0 || path.length > 512)
    return false;
  if (path.charAt(0) !== "/") return false;
  if (path.charAt(1) === "/" || path.charAt(1) === "\\") return false;
  if (!SAFE_PATH.test(path)) return false;
  if (path === "/auth" || path.indexOf("/auth/") === 0) return false;
  // "/" is never a destination — app/index.js redirects the bare root straight
  // to /auth/login. Stashing it would mean the login page redirects back to "/",
  // which redirects to the login page: a cycle React ends with "Maximum update
  // depth exceeded", and there are no error boundaries to catch it.
  if (path === "/") return false;
  return true;
}

// Who was signed in when the destination was recorded, or null if nobody was.
function currentUserId() {
  try {
    return window.localStorage.getItem("user") || null;
  } catch (e) {
    return null;
  }
}

// Has THIS running app instance ever seen a signed-in user? Reset to false on
// every full document load, which is exactly the boundary we need:
//   - A genuine deep-link click is a fresh document load, so this is false when
//     the anonymous stash is written — a real "nobody signed in" arrival.
//   - An explicit logout is a same-document history.push, so this stays true;
//     the Back button then re-navigates to the page the user just left and fires
//     stashRedirect again with auth state already wiped. That looks identical to
//     an anonymous deep link and would be honoured for whoever signs in next.
//     This flag is the one thing that tells the two apart.
//   - A session-expiry (401) eject is a full reload, so this resets to false —
//     but the pre-existing userId-tagged stash is untouched, so same-user resume
//     still works via the userId match in readRedirect.
let sawAuthenticatedUser = false;

export function stashRedirect(path) {
  try {
    const userId = currentUserId();
    // Capture BEFORE we flip it: was a session already established when this
    // stash was taken? That is the signal that separates a real mid-session
    // location from a boot-time arrival.
    const midSession = sawAuthenticatedUser;
    if (userId) sawAuthenticatedUser = true;
    // A rejected path is a NO-OP and must never removeItem. Every eject
    // re-renders at /auth/login, which re-invokes this; deleting on rejection
    // would destroy the very destination we are trying to hold on to.
    if (!isSafeRedirect(path)) return;
    // Post-logout Back-button residue: an unauthenticated stash from an instance
    // that WAS authenticated is the page the just-logged-out user left, not a
    // deep link. Dropping it stops it becoming the next sign-in's landing page.
    if (!userId && midSession) return;
    // Scope the stash to a user ONLY when it was recorded mid-session. A
    // boot-time arrival — a deep link — has no live session even if a STALE
    // token+user for someone else is still in localStorage (common on a shared
    // terminal); tagging it with that stale id would make the clicker lose their
    // own link. So a boot arrival is anonymous and honoured for whoever signs in.
    const owner = midSession ? userId : null;
    sessionStorage.setItem(
      KEY,
      JSON.stringify({ path, ts: Date.now(), userId: owner })
    );
  } catch (e) {
    // Safari private mode and sandboxed contexts throw on write. The correct
    // degradation is today's behavior: land on /solutions.
  }
}

function readRedirect() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const { path, ts, userId } = JSON.parse(raw);
    // Re-validate on the way out: the guard may have tightened since the write,
    // and this is user-reachable storage.
    if (!isSafeRedirect(path)) return null;
    if (typeof ts !== "number" || Date.now() - ts > TTL_MS) return null;
    // A destination recorded while somebody was signed in belongs to THEM.
    // sessionStorage outlives the sign-out navigation, so on a shared machine
    // this is what stops user A's last page from becoming user B's landing page
    // after B signs in on the same tab. A destination recorded with nobody
    // signed in (userId null) is an anonymous arrival — the emailed deep link
    // this whole mechanism exists for — and is honoured for whoever signs in.
    if (userId && userId !== currentUserId()) return null;
    return path;
  } catch (e) {
    return null;
  }
}

// Non-consuming — for the login banner, which renders on both /auth/login and
// /auth/validate and must survive the 2FA hop between them.
export function peekRedirect() {
  return readRedirect();
}

// Read and delete. The destination is single-use: once we have landed the user
// on it, a later sign-in in this tab must go to /solutions as usual.
export function consumeRedirect() {
  const path = readRedirect();
  clearRedirect();
  return path;
}

export function clearRedirect() {
  try {
    sessionStorage.removeItem(KEY);
  } catch (e) {
    // Nothing to do — a stash we cannot clear is TTL-bounded anyway.
  }
}
